import { Header, Icons, Logo } from '@service-victoria/component-library';
import Spinner from '@service-victoria/component-library/components/Icons/Spinner';
import Text from '@service-victoria/component-library/components/Text';
import { constants, logger } from 'app/utils';
import Field from './Field';
import * as Styled from './styled';
import DashboardButton from './DashboardButton';
import { app as Context } from 'app/context';

import PartnerLogo from 'app/assets/get-active-victoria-logo.png';
import { useActivityProvider } from 'app/hooks';
import { Address } from 'app/interfaces/api';
import { userManager } from 'app/services';
import { IdTokenClaims } from 'oidc-client-ts';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Availability, TransactionType } from 'app/interfaces/common';
import DashboardSelect from './DashboardSelect';
import useAvailable from 'app/hooks/useAvailable';

const getFullAddress = (address: Address | undefined): string =>
  `${address?.street}, ${address?.city}, ${address?.state} ${address?.postcode}`;

const Home: React.FC = () => {
  const history = useHistory();
  const { providerDetails, availableProviders, selectActivityProvider } = useActivityProvider();
  const { actions, currentState } = Context.useAppContext();

  const [profile, setProfile] = useState<IdTokenClaims | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useAvailable(false);
  const isAvailable = currentState.availability === Availability.Open;

  useEffect(() => {
    const load = async () => {
      const user = await userManager.getUserData();

      setProfile(user?.profile);
    };

    load();
  }, [history]);

  /**
   * Retry getting the selected activity provider's information.
   */
  async function retry(providerId: string, retryCount = 0) {
    if (retryCount >= constants.MAX_RETRIES) {
      logger.error('Error selecting Activity Provider');
      setLoading(false);
    } else {
      await handleClick(providerId, retryCount);
    }
  }

  /**
   * Handle click when selecting an activity provider.
   */
  async function handleClick(providerId: string, retryCount = 0) {
    try {
      if (providerDetails?.id === providerId) {
        // No change in selected provider, do nothing.
        return;
      }
      setLoading(true);
      const response = await selectActivityProvider(providerId);

      if (response === constants.SelectActivityProviderStatus.Success) {
        setLoading(false);
        return;
      }
      await retry(providerId, retryCount);
    } catch (error) {
      await retry(providerId, retryCount);
    }
  }

  function handleTileClick(transactionType) {
    if (transactionType) {
      switch (transactionType) {
        case TransactionType.ProviderDetails:
          history.push(`${constants.Routes.ProviderDetailsTransaction}#providerDetails`);
          break;
        case TransactionType.Redemption:
          history.push(`${constants.Routes.Redemption}#redeem`);
          break;
      }
      actions.startTransaction(transactionType);
    }
  }

  const headerProps = { ...constants.headerProps, ...userManager.signinHeaderProps };

  return (
    <>
      <Header {...headerProps} />
      {loading && (
        <Styled.Loader>
          <Spinner />
        </Styled.Loader>
      )}
      <Styled.NavWrapper>
        <Styled.Nav>
          <span>Hi, {profile?.given_name || ''}</span>
          {availableProviders && availableProviders?.length === 1 && (
            <Styled.ProviderNameWrapper data-testid='selectedOrganisationName'>
              {providerDetails?.organisation.name}
            </Styled.ProviderNameWrapper>
          )}
          {availableProviders && availableProviders?.length > 1 && (
            <DashboardSelect providers={availableProviders} handleClick={handleClick} />
          )}
        </Styled.Nav>
      </Styled.NavWrapper>
      <Styled.HeaderWrapper>
        <Styled.Header>
          <Styled.Heading>{providerDetails?.organisation.name}</Styled.Heading>
          <Styled.Description>
            Upload and redeem your Get Active Kids vouchers here. You can enter your vouchers
            individually or upload a CSV file of up to 50 vouchers.
          </Styled.Description>
          <Styled.ClosureContentWrapper>
            {!isAvailable && (
              <Styled.Banner>
                <Styled.AlertIcon />
                Get Active Kids Voucher redemption is now closed. You can still update your
                organisation details and change your authorised contact.&nbsp;
                <Text.Link href={constants.OUTDOOR_GAK_PATH}>Learn more</Text.Link>
              </Styled.Banner>
            )}
            <Styled.RedeemButton
              disabled={!isAvailable}
              onClick={() => handleTileClick(TransactionType.Redemption)}>
              Redeem vouchers
            </Styled.RedeemButton>{' '}
          </Styled.ClosureContentWrapper>
        </Styled.Header>
      </Styled.HeaderWrapper>
      <Styled.ContentWrapper>
        <Styled.Content>
          <Styled.ColumnWrapper>
            <Styled.SubHeading>Review your details</Styled.SubHeading>
            <Styled.Section>
              <Styled.SectionHeading>Organisation Details</Styled.SectionHeading>
              <Field
                label='Name'
                value={providerDetails?.organisation.name}
                dataTestId='organisationName'
              />
              <Field label='Activity type' value={providerDetails?.organisation.activityType} />
              <Field
                label='Address'
                value={getFullAddress(providerDetails?.organisation.address)}
              />
              <Field label='Phone number' value={providerDetails?.organisation.phone} />
            </Styled.Section>
            <Styled.Section>
              <Styled.SectionHeading>Authorised Contact</Styled.SectionHeading>
              <Field label='Given name(s)' value={profile?.given_name} dataTestId='givenName' />
              <Field label='Family name' value={profile?.family_name} dataTestId='familyName' />
              <Field label='Role' value={providerDetails?.authorisedContact.role} />
              <Field label='Email' value={profile?.email} dataTestId='email' />
            </Styled.Section>
          </Styled.ColumnWrapper>
          <Styled.ColumnWrapper>
            <Styled.SubHeading>Update your details</Styled.SubHeading>
            <DashboardButton
              title='Update organisation details'
              description="Update your organisation's details, including name, phone number, activity type and address."
              icon={Icons.Change}
              onClick={() => handleTileClick(TransactionType.ProviderDetails)}
            />
            <DashboardButton
              title='Change authorised contact'
              description='Transfer your Get Active Kids account to someone else, or update your personal details.'
              icon={Icons.Change}
              onClick={() => history.push(`${constants.Routes.ChangeAuthorisedContact}`)}
            />
          </Styled.ColumnWrapper>
        </Styled.Content>
        <Styled.Footer>
          <img src={PartnerLogo} alt='Get Active Victoria' />
          <Logo />
        </Styled.Footer>
      </Styled.ContentWrapper>
    </>
  );
};

export default Home;
